import { Component, effect, EventEmitter, inject, input, Input, Output, viewChild } from '@angular/core';
import { trimmedMinLength } from '../form/validators/trimmedMinLength.validator';
import { IBusinessData, IFormInput, IFormModel, IFormSelect, KundenTypEnum } from '@mwe/models';
import {
  CountryLogic,
  getB2BCorporateRegisterNumberValidators,
  getB2BNameValidators,
  getB2BUidNumberValidators,
  isArrayWithMinOneItem,
} from '@mwe/utils';
import { Validators } from '@angular/forms';
import { FormComponent } from '../form/form.component';

@Component({
  selector: 'mwe-business-details-form',
  templateUrl: './business-details-form.component.html',
  imports: [FormComponent],
})
export class BusinessDetailsFormComponent {
  private countryLogic = inject(CountryLogic);

  formRef = viewChild(FormComponent);
  emailInputs = input<IFormInput[]>([]);
  @Input() businessData: IBusinessData;
  @Input() fixedEmail = '';
  @Input() isPhoneRequired = true;
  @Input() isPhoneDisabled = false;
  @Input() isCompanyNameDisabled = false;
  @Input() isUidNumberDisabled = false;
  @Input() isCorporateNumberDisabled = false;
  @Input() removeEmailHelpText = true;

  @Output() updateBusinessData = new EventEmitter<IBusinessData>();
  @Output() formDataUpdated = new EventEmitter();

  formModel: IFormModel;
  isValid = false;

  get areEmailFieldsVisible(): boolean {
    return isArrayWithMinOneItem(this.emailInputs());
  }

  constructor() {
    effect(() => {
      this.initializeFormModel(this.emailInputs());
    });
  }

  handleFormValidation(formData?: any): void {
    if (!this.formRef()?.form || !formData) {
      return;
    }

    const telefonCountryCode = (formData['mwe-order-business-data-telefon-countrycode'] as IFormSelect)?.value;

    this.businessData = {
      companyName: formData['mwe-order-business-data-company-name'] ?? this.businessData.companyName,
      uidNumber: formData['mwe-order-business-data-uid-number'] ?? '',
      corporateRegisterNumber: formData['mwe-order-business-data-corporate-register-number'] ?? '',
      telefonNumber: formData['mwe-order-business-data-telefonnumber'],
      telefonCountryCode,
      email: this.areEmailFieldsVisible ? formData[this.emailInputs()?.[0]?.name] : this.fixedEmail,
      emailRepeat: this.areEmailFieldsVisible ? formData[this.emailInputs()?.[1]?.name] : this.fixedEmail,
    };

    this.updateBusinessData.emit(this.businessData);
    this.formDataUpdated.emit(formData);
  }

  validate(): boolean {
    this.formRef()?.fireValidation();
    return this.formRef()?.isValid();
  }

  private initializeFormModel(emailInputs: IFormInput[] = []): void {
    if (!this.businessData) {
      this.businessData = {
        type: '' as KundenTypEnum,
        companyName: '',
        corporateRegisterNumber: '',
        uidNumber: '',
        email: this.fixedEmail,
        emailRepeat: this.fixedEmail,
        telefonCountryCode: this.isPhoneRequired ? '43' : '',
        telefonNumber: '',
      };
    }

    if (this.isPhoneRequired) {
      this.businessData.telefonCountryCode = this.countryLogic.checkCountryCode(this.businessData.telefonCountryCode);
    }

    this.formModel = {
      inputs: [
        {
          name: 'mwe-order-business-data-company-name',
          initialValue: this.businessData.companyName,
          validators: [Validators.required, trimmedMinLength(2), getB2BNameValidators()],
          labelKey: 'newClient.businessDetails.companyName.label',
          validationErrorLabelKey: 'newClient.businessDetails.companyName.error',
          validate: true,
          infoTextCode: 'kundenInformationen.firmenName.error_firmenname',
          isReadonly: this.isCompanyNameDisabled,
          maxLength: 200,
        },
        {
          name: 'mwe-order-business-data-uid-coporate-container',
          inputs: [
            {
              name: 'mwe-order-business-data-uid-number',
              helpLabelKey: 'newClient.businessDetails.uidNumber.helpLabel',
              initialValue: this.businessData.uidNumber,
              validate: true,
              validators: [getB2BUidNumberValidators()],
              labelKey: 'newClient.businessDetails.uidNumber.label',
              validationErrorLabelKey: 'newClient.businessDetails.uidNumber.error',
              inputClasses: 'col-12 col-md-6',
              isReadonly: this.isUidNumberDisabled,
            },
            {
              name: 'mwe-order-business-data-corporate-register-number',
              helpLabelKey: 'newClient.businessDetails.corporateRegisterNumber.helpLabel',
              initialValue: this.businessData.corporateRegisterNumber,
              validate: true,
              validators: [getB2BCorporateRegisterNumberValidators()],
              labelKey: 'newClient.businessDetails.corporateRegisterNumber.label',
              validationErrorLabelKey: 'newClient.businessDetails.corporateRegisterNumber.error',
              inputClasses: 'col-12 col-md-6',
              isReadonly: this.isCorporateNumberDisabled,
            },
          ],
        },
        this.countryLogic.getPhoneFormInputGroup({
          countryCode: this.businessData.telefonCountryCode,
          phoneNumber: this.businessData.telefonNumber,
          isDisabled: this.isPhoneDisabled,
          namePrefix: 'mwe-order-business-data',
          labelPrefix: 'newClient.businessDetails',
          isRequired: this.isPhoneRequired,
        }),
        ...emailInputs,
      ],
    };
  }
}
